import React, { useState, useContext, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Building2, Shield, Loader2, ChevronDown, Info, MapPin, PlusCircle, Building } from 'lucide-react';
import Tooltip from '../Shared/Tooltip';
import { AuthContext } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import siteService from '../../services/siteService';
import toast from 'react-hot-toast';

const AddUserModal = ({ isOpen, onClose, onSuccess, sponsorId, studyId, siteId = null, availableRoles = [] }) => {
    const { user } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        is_internal: false,
        sponsor_id: sponsorId,
        study_id: studyId,
        site_ids: siteId ? [siteId] : []
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableSites, setAvailableSites] = useState([]);
    const [loadingSites, setLoadingSites] = useState(false);
    const [isFocused, setIsFocused] = useState({
        email: false,
        firstName: false,
        lastName: false,
        role: false
    });

    useEffect(() => {
        setFormData(prevData => ({ 
            ...prevData, 
            sponsor_id: sponsorId 
        }));
    }, [sponsorId]);

    useEffect(() => {
        setFormData(prevData => ({ 
            ...prevData, 
            study_id: studyId,
            site_ids: siteId ? [siteId] : prevData.site_ids
        }));
    }, [studyId, siteId]);

    // Fetch available sites when studyId is provided and modal is open
    useEffect(() => {
        const fetchSites = async () => {
            if (studyId && isOpen && !siteId) { // Only fetch if we're in study-level mode
                setLoadingSites(true);
                try {
                    const sites = await siteService.getSitesForStudy(studyId);
                    setAvailableSites(sites);
                } catch (error) {
                    console.error('Error fetching sites:', error);
                    setAvailableSites([]);
                } finally {
                    setLoadingSites(false);
                }
            }
        };
        fetchSites();
    }, [studyId, isOpen, siteId]);

    // Required field marker component
    const RequiredFieldMarker = () => (
        <span className="text-red-500 ml-1 text-xs" aria-label="required field">*</span>
    );

    const closeDialog = () => {
        // reset form data
        setFormData({
            email: '',
            firstName: '',
            lastName: '',
            role: '',
            is_internal: false,
            sponsor_id: formData.sponsor_id,
            study_id: formData.study_id,
            site_ids: siteId ? [siteId] : []
        });
        
        // Reset focus states
        setIsFocused({
            email: false,
            firstName: false,
            lastName: false,
            role: false
        });
        
        setError('');
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Require at least one site for site-scoped roles when adding at study level
        if (studyId && !siteId && roleRequiresSiteSelection && formData.site_ids.length === 0) {
            setError('Please select at least one site for this role. Study Coordinators and Investigators must be assigned to specific sites.');
            setLoading(false);
            return;
        }

        try {
            // Debug logging to see what's being sent
            console.log('[ADD_USER_MODAL] Form data being submitted:', formData);
            const response = await userService.addUser(formData);
            onSuccess();
            closeDialog();
            
            toast.success(`User ${formData.email} was created successfully`, {
                style: {
                    borderRadius: '10px',
                    background: '#10B981',
                    color: '#fff',
                }
            });
            
        } catch (error) {
            console.error('Error adding user:', error);
            console.log('Error response data:', error.response?.data);
            console.log('Error response status:', error.response?.status);
            
            // Extract error message with multiple fallbacks
            let errorMessage = error.response?.data?.__all__?.[0] || 
                              error.response?.data?.detail ||
                              error.response?.data?.error ||
                              error.message ||
                              'Failed to create user';
            
            console.log('Extracted error message:', errorMessage);
            
            // Handle different error message formats
            let finalErrorMessage = errorMessage;
            
            // Check if it's the email already registered error
            if (errorMessage && (errorMessage.includes('Email is already registered') || errorMessage.includes('already registered'))) {
                // Try to extract email from different formats
                let email = formData.email; // fallback to form email
                
                if (errorMessage.includes(': ')) {
                    const parts = errorMessage.split(': ');
                    if (parts.length > 1) {
                        email = parts[parts.length - 1]; // get the last part after colon
                    }
                }
                
                finalErrorMessage = `This email address (${email}) is already registered in the system. Please use a different email address or contact an administrator if you believe this is an error.`;
                console.log('Updated user-friendly message:', finalErrorMessage);
            }
            
            errorMessage = finalErrorMessage;
            
            setError(errorMessage);
            
            toast.error(errorMessage, {
                style: {
                    borderRadius: '10px',
                    background: '#EF4444',
                    color: '#fff',
                }
            });
            
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSiteSelection = (siteId) => {
        setFormData(prevData => {
            const newSiteIds = prevData.site_ids.includes(siteId)
                ? prevData.site_ids.filter(id => id !== siteId)
                : [...prevData.site_ids, siteId];
            return {
                ...prevData,
                site_ids: newSiteIds
            };
        });
    };

    const handleSelectAllSites = () => {
        const allSiteIds = availableSites.map(site => site.id);
        const isAllSelected = allSiteIds.every(id => formData.site_ids.includes(id));
        
        setFormData(prevData => ({
            ...prevData,
            site_ids: isAllSelected ? [] : allSiteIds
        }));
    };

    // Check if role requires site selection
    const roleRequiresSiteSelection = ['investigator', 'study_coordinator'].includes(formData.role);

    const handleFocus = (field) => {
        setIsFocused(prev => ({
            ...prev,
            [field]: true
        }));
    };

    const handleBlur = (field) => {
        setIsFocused(prev => ({
            ...prev,
            [field]: false
        }));
    };

    // Animation variants
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { duration: 0.3 }
        },
        exit: { 
            opacity: 0,
            transition: { duration: 0.2 }
        }
    };

    const modalVariants = {
        hidden: { 
            opacity: 0, 
            scale: 0.95,
            y: 10
        },
        visible: { 
            opacity: 1, 
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 400
            }
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 10,
            transition: {
                duration: 0.2
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <Dialog
                    as={motion.div}
                    static
                    open={isOpen}
                    onClose={closeDialog}
                    className="relative z-50"
                >
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={overlayVariants}
                        className="fixed inset-0 bg-black/25 backdrop-blur-sm"
                        aria-hidden="true"
                    />
                    
                    <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
                        <Dialog.Panel
                            as={motion.div}
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden"
                        >
                            {/* Header */}
                            <div className="relative bg-primary-600 dark:bg-primary-700 px-6 py-5">
                                <div className="flex justify-between items-center">
                                    <Dialog.Title className="text-xl font-semibold text-white flex items-center">
                                        <PlusCircle className="h-5 w-5 mr-2 text-white opacity-90" />
                                        Add New User
                                    </Dialog.Title>
                                    <button
                                        onClick={closeDialog}
                                        className="text-white/80 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-full focus:outline-none focus:ring-2 focus:ring-white/30"
                                        aria-label="Close"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            <div className="px-6 pt-6 pb-8">
                                {/* Info notice */}
                                <div className="mb-6 p-3.5 bg-blue-50 border border-blue-100 text-blue-800 rounded-xl text-sm flex items-start">
                                    <Info className="h-5 w-5 mr-2.5 flex-shrink-0 mt-0.5 text-blue-500" />
                                    <p className="leading-relaxed">
                                        An authentication link will be emailed to the user. They can click the link to set their password and activate their account.
                                    </p>
                                </div>
                                
                                {/* Error message */}
                                {error && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mb-6 p-3.5 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm flex items-start"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2.5 flex-shrink-0 mt-0.5 text-red-500">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        <p>{error}</p>
                                    </motion.div>
                                )}
                                
                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {/* Email field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                                            Email<RequiredFieldMarker />
                                        </label>
                                        <div className="relative group">
                                            <Mail className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 transition-colors duration-200 ${
                                                isFocused.email ? 'text-primary-500' : 'text-gray-400'
                                            }`} />
                                            <input
                                                type="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                onFocus={() => handleFocus('email')}
                                                onBlur={() => handleBlur('email')}
                                                className={`pl-11 w-full rounded-xl border bg-white px-3.5 py-2.5 text-gray-900 placeholder:text-gray-400 outline-none transition-all duration-200 ${
                                                    isFocused.email 
                                                        ? 'border-primary-500 ring-4 ring-primary-500/15' 
                                                        : 'border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15'
                                                }`}
                                                placeholder="user@example.com"
                                            />
                                        </div>
                                    </div>

                                    {/* Name fields */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* First name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                                                First Name<RequiredFieldMarker />
                                            </label>
                                            <div className="relative group">
                                                <User className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 transition-colors duration-200 ${
                                                    isFocused.firstName ? 'text-primary-500' : 'text-gray-400'
                                                }`} />
                                                <input
                                                    type="text"
                                                    name="firstName"
                                                    required
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('firstName')}
                                                    onBlur={() => handleBlur('firstName')}
                                                    className={`pl-11 w-full rounded-xl border bg-white px-3.5 py-2.5 text-gray-900 placeholder:text-gray-400 outline-none transition-all duration-200 ${
                                                        isFocused.firstName 
                                                            ? 'border-primary-500 ring-4 ring-primary-500/15' 
                                                            : 'border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15'
                                                    }`}
                                                    placeholder="First name"
                                                />
                                            </div>
                                        </div>

                                        {/* Last name */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                                                Last Name<RequiredFieldMarker />
                                            </label>
                                            <div className="relative group">
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    required
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('lastName')}
                                                    onBlur={() => handleBlur('lastName')}
                                                    className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-gray-900 placeholder:text-gray-400 outline-none transition-all duration-200 ${
                                                        isFocused.lastName 
                                                            ? 'border-primary-500 ring-4 ring-primary-500/15' 
                                                            : 'border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15'
                                                    }`}
                                                    placeholder="Last name"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Role field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                                            Role<RequiredFieldMarker />
                                        </label>
                                        <div className="relative group">
                                            <Building className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 transition-colors duration-200 ${
                                                isFocused.role ? 'text-primary-500' : 'text-gray-400'
                                            }`} />
                                            <select
                                                name="role"
                                                required
                                                value={formData.role}
                                                onChange={handleChange}
                                                onFocus={() => handleFocus('role')}
                                                onBlur={() => handleBlur('role')}
                                                className={`pl-11 w-full rounded-xl border bg-white px-3.5 py-2.5 text-gray-900 appearance-none outline-none transition-all duration-200 ${
                                                    isFocused.role 
                                                        ? 'border-primary-500 ring-4 ring-primary-500/15' 
                                                        : 'border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15'
                                                }`}
                                            >
                                                <option value="">Select a role</option>
                                                {availableRoles && availableRoles.length > 0 ? (
                                                    availableRoles.map((role) => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))
                                                ) : (
                                                    userService.getRoles(user).map((role) => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                                                <ChevronDown className="h-4.5 w-4.5" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Site Selection - only show when in study-level mode (not single site) and role requires site selection */}
                                    {studyId && !siteId && roleRequiresSiteSelection && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center">
                                                Site Access
                                                <Tooltip 
                                                    content="Select one or more sites this user should have access to. Investigators and Study Coordinators typically have access to individual sites."
                                                    position="bottom"
                                                    iconClassName="ml-2 h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help"
                                                />
                                            </label>
                                            {availableSites.length > 0 && (
                                                <div className="flex justify-between items-center mb-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleSelectAllSites}
                                                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                                                    >
                                                        {availableSites.every(site => formData.site_ids.includes(site.id)) ? 'Deselect All' : 'Select All'}
                                                    </button>
                                                </div>
                                            )}
                                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50">
                                                {loadingSites ? (
                                                    <div className="flex items-center justify-center py-4">
                                                        <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
                                                        <span className="ml-2 text-sm text-gray-500">Loading sites...</span>
                                                    </div>
                                                ) : availableSites.length === 0 ? (
                                                    <div className="text-sm text-gray-500 text-center py-4">
                                                        No sites available for this study
                                                    </div>
                                                ) : (
                                                    availableSites.map((site) => (
                                                        <div key={site.id} className="flex items-center space-x-3">
                                                            <input
                                                                type="checkbox"
                                                                id={`site-${site.id}`}
                                                                checked={formData.site_ids.includes(site.id)}
                                                                onChange={() => handleSiteSelection(site.id)}
                                                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                                                            />
                                                            <label 
                                                                htmlFor={`site-${site.id}`} 
                                                                className="flex-1 text-sm text-gray-700 cursor-pointer flex items-center"
                                                            >
                                                                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                                                {site.site_name}
                                                            </label>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {siteId ? "This user will be added to the current site only." : "Select one or more sites this user should have access to. Investigators and Study Coordinators typically have access to individual sites."}
                                            </p>
                                        </div>
                                    )}

                                    {/* Single Site Mode Info */}
                                    {siteId && (
                                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                                            <div className="flex items-start space-x-3">
                                                <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                                                <div>
                                                    <h4 className="text-sm font-medium text-blue-900">Adding User to Specific Site</h4>
                                                    <p className="text-sm text-blue-700 mt-1">
                                                        This user will be granted access to this site only. They will automatically be assigned to the study as well.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Study Team Internal/External Checkbox - only show when Study Team is selected */}
                                    {formData.role === 'study_team' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="space-y-3"
                                        >
                                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                                <div className="flex items-start space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        id="is_internal"
                                                        name="is_internal"
                                                        checked={formData.is_internal}
                                                        onChange={handleChange}
                                                        className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                                                    />
                                                    <div className="flex-1">
                                                        <label htmlFor="is_internal" className="block text-sm font-medium text-gray-700 cursor-pointer">
                                                            Internal Study Team Member
                                                        </label>
                                                        <p className="text-xs text-gray-600 mt-1">
                                                            Check this box if the Study Team member is an internal employee who requires multi-factor authentication (MFA). Leave unchecked for external users.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Submit button */}
                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full flex justify-center items-center px-4 py-3 rounded-xl bg-primary-600 text-white font-medium shadow-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-600/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                                        >
                                            {loading ? (
                                                <div className="flex items-center">
                                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                                    <span>Adding...</span>
                                                </div>
                                            ) : (
                                                'Add User'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AddUserModal;